import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { espaceSalarieApi, BulletinDetail } from '../api/espaceSalarieApi';
import { bulletinsApi } from '../../bulletins/api/bulletins.api';

const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const f = (n: number | string) => Number(n).toLocaleString('fr-FR') + ' F';

const CATEGORIE_LABELS: Record<string, string> = {
  SALAIRE_BASE: 'Salaire de base',
  INDEMNITE: 'Indemnités',
  PRIME: 'Primes',
  HEURES_SUPPLEMENTAIRES: 'Heures supplémentaires',
  RETENUE: 'Retenues salariales',
  TAXE: 'Taxes',
  COTISATION: 'Cotisations',
  AVANCE: 'Avances',
  OPPOSITION: 'Oppositions',
  AJUSTEMENT: 'Ajustements',
  TOTAL: 'Totaux',
};

export function MonBulletinDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [bulletin, setBulletin] = useState<BulletinDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [telechargementPDF, setTelechargementPDF] = useState(false);
  const [erreurPDF, setErreurPDF] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    espaceSalarieApi.obtenirMonBulletin(id)
      .then(data => {
        setBulletin(data);
        setErreur(null);
      })
      .catch(() => setErreur('Impossible de charger ce bulletin.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-32" />
          <div className="h-48 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (erreur || !bulletin) {
    return (
      <div className="max-w-3xl mx-auto">
        <Link to="/espace-salarie" className="text-sm text-slate-400 hover:text-slate-600 mb-4 inline-block">
          ← Retour
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-red-700 font-semibold">{erreur ?? 'Bulletin introuvable'}</p>
        </div>
      </div>
    );
  }

  const handleTelechargerPDF = async () => {
    if (!id) return;
    setTelechargementPDF(true);
    setErreurPDF(null);
    try {
      const blob = await bulletinsApi.telechargerPDF(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin-${bulletin.mois}-${bulletin.annee}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setErreurPDF(err?.response?.data?.message || err?.message || 'Erreur lors du téléchargement');
    } finally {
      setTelechargementPDF(false);
    }
  };

  const lignesVisibles = bulletin.lignesCalcul.filter(l => l.afficher);

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/espace-salarie" className="text-sm text-slate-400 hover:text-slate-600 mb-4 inline-block">
        ← Mes bulletins
      </Link>

      <h1 className="text-2xl font-bold text-slate-900 mb-1">
        Bulletin — {MOIS_NOMS[bulletin.mois - 1]} {bulletin.annee}
      </h1>
      {bulletin.employe && (
        <p className="text-slate-500 mb-4">
          {bulletin.employe.nom} {bulletin.employe.prenoms} — {bulletin.employe.fonction}
        </p>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleTelechargerPDF}
          disabled={telechargementPDF}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {telechargementPDF ? 'Génération...' : '⬇ Télécharger PDF'}
        </button>
        {erreurPDF && <span className="text-xs text-red-600">{erreurPDF}</span>}
      </div>

      {/* Récapitulatif */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Salaire brut', value: f(bulletin.salaireBrut), accent: false },
          { label: 'Salaire net', value: f(bulletin.salaireNet), accent: false },
          { label: 'Net à payer', value: f(bulletin.netAPayer), accent: true },
          { label: 'Statut', value: bulletin.statut, accent: false },
        ].map(({ label, value, accent }) => (
          <div key={label} className={`rounded-xl p-4 border ${accent ? 'bg-emerald-50 border-emerald-200' : 'bg-surface border-slate-200'}`}>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-lg font-bold ${accent ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Lignes de calcul */}
      <div className="bg-surface border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Détail du bulletin</h2>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Libellé</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Catégorie</th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lignesVisibles.map(l => (
              <tr key={l.idLigne} className="hover:bg-slate-50/50">
                <td className="px-5 py-3 text-sm text-slate-800">{l.libelle}</td>
                <td className="px-5 py-3 text-xs text-slate-500">{CATEGORIE_LABELS[l.categorie] ?? l.categorie}</td>
                <td className="px-5 py-3 text-sm font-mono tabular-nums text-right text-slate-900">
                  {f(l.montant)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MonBulletinDetailPage;
